/**
 * DashboardModeToggle — Toggle para alternar entre modo Explicativo e Compacto
 *
 * Modo Explicativo: exibe tooltips, descrições e dicas contextuais nos dashboards
 * Modo Compacto: exibe apenas os dados essenciais, sem textos explicativos
 *
 * A preferência é salva automaticamente no localStorage.
 */
import { BookOpen, LayoutDashboard } from "lucide-react";
import { useUI } from "@/contexts/UIContext";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface DashboardModeToggleProps {
  className?: string;
}

export default function DashboardModeToggle({ className = "" }: DashboardModeToggleProps) {
  const { dashboardMode, toggleDashboardMode, isExplicativo } = useUI();

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={toggleDashboardMode}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
              isExplicativo
                ? "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                : "bg-muted text-muted-foreground border-border hover:bg-muted/80"
            } ${className}`}
            aria-label={`Modo atual: ${isExplicativo ? "Explicativo" : "Compacto"}. Clique para alternar.`}
          >
            {isExplicativo ? (
              <BookOpen size={13} className="shrink-0" />
            ) : (
              <LayoutDashboard size={13} className="shrink-0" />
            )}
            <span>{isExplicativo ? "Explicativo" : "Compacto"}</span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="space-y-1">
            <p className="text-xs font-semibold">
              {isExplicativo ? "Modo Explicativo ativo" : "Modo Compacto ativo"}
            </p>
            <p className="text-xs text-muted-foreground">
              {isExplicativo
                ? "Tooltips e descrições contextuais estão visíveis. Clique para ocultar e ver apenas os dados."
                : "Apenas dados essenciais são exibidos. Clique para ativar tooltips e descrições contextuais."}
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
